import React, { useState, useEffect } from "react";
import LeaderboardEntryComponent from "../components/Leaderboard/LeaderboardEntry";
import styled, { keyframes } from "styled-components";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { RootState } from "../redux/type";
import { useDispatch } from "react-redux";
import { getFriends } from "../api/user";
import { motion } from "framer-motion";
import { setFriends } from "../redux/actions";

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 224, 255, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(0, 224, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 224, 255, 0); }
`;

// Styled Components
const ClubWrapper = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  padding-bottom: 180px;
`;

const ClubContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, #ccff00, #00b4db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.5rem;
`;

const FriendsCount = styled.div`
  background: rgba(0, 224, 255, 0.1);
  color: #ccff00;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid rgba(0, 224, 255, 0.3);
  align-self: flex-start;
  margin-bottom: 1rem;
`;

const InviteButtonContainer = styled(motion.div)`
  gap: 1rem;
  background: #121212;
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;
  margin: 2rem 0;
  border-radius: 12px;
`;

const ActionButton = styled(motion.button)`
  padding: 1rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
`;

const CopyButton = styled(ActionButton)`
  background: linear-gradient(135deg, #ccff00, #00b4db);
  color: #111;
`;

const InviteButton = styled(ActionButton)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FriendIllustration = styled(motion.div)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 100px;
  height: 100px;
  opacity: 0.1;
  z-index: -1;
  animation: ${float} 6s ease-in-out infinite;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  gap: 1rem;
`;

const InviteButtons = ({ userId }) => {
  const link = `https://t.me/PlayOpsARGTerminalBot?start=ref_${userId}`;
  const inviteLink = `https://telegram.me/share/url?url=${link}&text=Join%20Paws%20and%20earn%20rewards%20for%20completing%20tasks!`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Copied invite link!", {
        icon: "📋",
        style: {
          borderRadius: "12px",
          background: "#333",
          color: "#fff",
        },
      });
    } catch (err) {
      toast.error("Failed to copy link", {
        icon: "❌",
      });
    }
  };

  return (
    <InviteButtonContainer
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <CopyButton
        onClick={handleCopy}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        Copy Invite Link
      </CopyButton>
      <InviteButton
        onClick={() => window.open(inviteLink, "_blank")}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        Invite via Telegram
      </InviteButton>
    </InviteButtonContainer>
  );
};

const Club = () => {
  const user = useSelector((state: RootState) => state.userData);
  const club = useSelector((state: RootState) => state.friends);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const friends = await getFriends(user.uid);

        if (friends) {
          dispatch(setFriends(friends));
        }
      } catch (error) {
        console.error("Failed to fetch friends:", error);
      }
    };

    fetchFriends();
  }, [user.uid, dispatch]);

  return (
    <>
      <ClubWrapper>
        <FriendIllustration>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
              fill="#ccff00"
            />
            <path
              d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z"
              fill="#ccff00"
            />
            <path
              d="M17 8C18.6569 8 20 6.65685 20 5C20 3.34315 18.6569 2 17 2C15.3431 2 14 3.34315 14 5C14 6.65685 15.3431 8 17 8Z"
              fill="#00B4DB"
            />
            <path
              d="M17 10C13.6863 10 11 12.6863 11 16H23C23 12.6863 20.3137 10 17 10Z"
              fill="#00B4DB"
            />
          </svg>
        </FriendIllustration>

        <Title>Friend Network</Title>
        <Subtitle>Earn 10% bonus PAWS when your friends play</Subtitle>
        <InviteButtons userId={user.uid} />

        <FriendsCount>
          {club?.length || 0} {club?.length === 1 ? "Buddy" : "Buddies"}
        </FriendsCount>

        <ClubContainer>
          {club?.length > 0 ? (
            club.map((friend, index) => (
              <motion.div
                key={friend.username}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <LeaderboardEntryComponent user={friend} index={index} />
              </motion.div>
            ))
          ) : (
            <EmptyState>
              <h3>No buddies yet</h3>
              <p>Invite friends to start earning bonuses</p>
            </EmptyState>
          )}
        </ClubContainer>
      </ClubWrapper>
    </>
  );
};

export default Club;
